import { useUnsavedChanges } from "../../helpers/unsaved-changes";
import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import {
  getApplicationRoles,
  getCompanyList,
  getCompanyLocations,
  getCompanySpecificLocations,
  getLoggedInUser,
  getuserById,
} from "../../helpers/backend_helper";
import { getData, postData } from "../../helpers/url-request";
import { showLoading } from "react-global-loading";
import "../Standards/Standards.scss";

const UserDetail = ({ userId, closeAction, firmId: initialFirmId, onEditUser }) => {
  const [currentFirmId, setCurrentFirmId] = useState(0);
  const [firms, setFirms] = useState([]);
  const [locations, setLocations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [canEditSharedProfile, setCanEditSharedProfile] = useState(false);
  const [userData, setUserData] = useState({
    id: 0,
    firmId: "",
    fullName: "",
    emailId: "",
    mobileNo: "",
    branchId: "",
    roleId: "",
    designation: "",
    newPassword: "",
    confPassword: "",
    disableUser: false,
    blockUser: false,
    changePassword: false,
  });
  const isEdit = Boolean(userId);
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const existingIdentity = !isEdit && lookup?.state && lookup.state !== "new";
  const needsPassword = !isEdit && !existingIdentity && !lookup?.requiresSsoIdentity;
  const checkIdentity = async (selectedFirmId = validation.values.firmId || currentFirmId) => {
    const email = validation.values.emailId.trim();
    const firmId = Number(selectedFirmId);
    setLookup(null);
    setLookupBusy(true); setLookupError("");
    try {
      const result = await postData("/FirmMembership/lookup", { email, firmId });
      if (!result.status) throw new Error(result.message || "Unable to check user.");
      validation.setFieldValue("sourceIdentityProvider", result.data?.federationProviderId || "");
      validation.setFieldValue("sourceIdentityObjectId", "");
      if (result.data?.userId) {
        validation.setFieldValue("fullName", result.data.fullName || "");
        validation.setFieldValue("mobileNo", result.data.mobileNo || "");
      }
      validation.setFieldValue("newPassword", ""); validation.setFieldValue("confPassword", "");
      setLookup({ ...result.data, email: email.toLowerCase(), firmId });
    } catch (e) { setLookupError(e.message); }
    finally { setLookupBusy(false); }
  };

  useEffect(() => {
    setCanEditSharedProfile(false);
    showLoading(true);
    Promise.all([
      getLoggedInUser(),
      getCompanyList(),
      getCompanyLocations(),
      getApplicationRoles(),
      userId ? (initialFirmId ? getData(`/User/userdetail/${userId}?firmId=${initialFirmId}`) : getuserById(userId)) : Promise.resolve(null),
    ])
      .then(
        ([
          currentUser,
          firmResponse,
          locationResponse,
          roleResponse,
          userResponse,
        ]) => {
          const firmId = currentUser?.firmId || 0;
          setCurrentFirmId(firmId);
          setFirms(firmResponse?.data || []);
          setLocations(locationResponse?.data || []);
          setRoles(roleResponse?.data || []);
          if (userResponse?.status) {
            const data = userResponse.data;
            setCanEditSharedProfile(data.canEditSharedProfile === true);
            const selectedFirmId =
              data.firmId || data.firmDetail?.id || firmId || "";
            setUserData({
              id: data.id,
              firmId: selectedFirmId,
              fullName: data.fullName || "",
              emailId: data.emailId || data.userLogin?.loginName || "",
              mobileNo: data.mobileNo || "",
              branchId:
                data.branchId || data.locationId || data.branchDetail?.id || "",
              roleId: data.roleId || data.role?.id || "",
              designation: data.designation || "",
              newPassword: "",
              confPassword: "",
              disableUser: data.status !== 1,
              blockUser: Boolean(data.blockUser),
              changePassword: Boolean(data.changePassword),
            });
            if (firmId === 1 && selectedFirmId)
              return getCompanySpecificLocations(selectedFirmId).then(
                (response) => setLocations(response?.data || [])
              );
          } else if (userResponse && !userResponse.status) {
            ErrorNotify(userResponse.message);
          }
        }
      )
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load the user details.");
      })
      .finally(() => showLoading(false));
  }, [userId, initialFirmId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: userData,
    validationSchema: Yup.object({
      fullName: canEditSharedProfile || (!isEdit && !existingIdentity) ? Yup.string().trim().max(200).required("Enter the user's full name") : Yup.string(),
      emailId: Yup.string()
        .email("Enter a valid email address")
        .required("Enter an email address"),
      mobileNo: canEditSharedProfile ? Yup.string().trim().required("Enter a contact number").matches(/^[+0-9() .\-]{3,30}$/, "Enter a valid contact number").test("digits", "Enter a valid contact number", (v) => (v?.match(/\d/g) || []).length >= 3) : !isEdit && !existingIdentity ? Yup.string().required("Enter a contact number") : Yup.string(),
      branchId: Yup.string().required("Select a branch"),
      designation: Yup.string().trim().required("Enter a designation"),
      roleId: Yup.string().required("Select a primary role"),
      ...(currentFirmId === 1
        ? { firmId: Yup.string().required("Select a firm") }
        : {}),
      ...(needsPassword
        ? {
            newPassword: Yup.string()
              .min(8, "Password must be at least 8 characters")
              .max(256, "Password must be 256 characters or fewer")
              .matches(/[a-z]/, "Include a lowercase letter")
              .matches(/[A-Z]/, "Include an uppercase letter")
              .matches(/[0-9]/, "Include a number")
              .matches(/[^A-Za-z0-9]/, "Include a symbol")
              .required("Enter a password"),
            confPassword: Yup.string()
              .oneOf([Yup.ref("newPassword")], "Passwords must match")
              .required("Confirm the password"),
          }
        : {}),
    }),
    onSubmit: (values) => {
      if (!isEdit && (lookupBusy || lookupError || !lookup || lookup.email !== values.emailId.trim().toLowerCase() || lookup.firmId !== Number(values.firmId || currentFirmId))) {
        setLookupError("Check this email and destination firm before continuing."); return;
      }
      if (!isEdit && ["already_member", "requires_reconciliation", "requires_reactivation"].includes(lookup?.state)) {
        setLookupError("This user needs membership review. Contact your PIERS administrator or edit their existing firm access."); return;
      }
      const request = {
        updateSharedProfile: isEdit && canEditSharedProfile,
        firmId: values.firmId || currentFirmId,
        userId: values.id || lookup?.userId || 0,
        sourceIdentityProvider: values.sourceIdentityProvider,
        sourceIdentityObjectId: values.sourceIdentityObjectId,
        fullName: values.fullName.trim(),
        emailId: values.emailId.trim(),
        mobileNo: values.mobileNo.trim(),
        newPassword: values.newPassword,
        confPassword: values.confPassword,
        designation: values.designation.trim(),
        branchId: values.branchId,
        roleId: values.roleId,
        disableUser: values.disableUser ? 1 : 0,
        blockUser: values.blockUser ? 1 : 0,
        changePassword: values.changePassword ? 1 : 0,
      };
      showLoading(true);
      postData(isEdit ? "/user/updateuser" : "/user/adduser", request)
        .then((response) => {
          if (response.status) {
            const provisioning = response?.data?.externalProvisioning;
            const message = isEdit
              ? canEditSharedProfile ? "User profile and firm access updated successfully." : "Firm membership updated successfully."
              : existingIdentity
              ? "User added to this firm."
              : provisioning === "provisioned" ||
                provisioning === "already_provisioned"
              ? "User invited and prepared for External ID activation."
              : provisioning &&
                provisioning !== "external_provisioning_disabled" &&
                provisioning !== "firm_external_authentication_not_enabled"
              ? `User created; External ID provisioning needs attention (${provisioning}).`
              : "User created successfully.";
            SuccessNotify(message);
            closeAction?.();
          } else {
            ErrorNotify(response.message);
          }
        })
        .catch((error) => {
          ErrorNotify(error?.message || "Unable to save the user.");
        })
        .finally(() => showLoading(false));
    },
  });

  const detailsDisabled = !isEdit && (
    lookupBusy || Boolean(lookupError) || !lookup ||
    !["new", "existing"].includes(lookup.state) ||
    lookup.email !== validation.values.emailId.trim().toLowerCase() ||
    lookup.firmId !== Number(validation.values.firmId || currentFirmId)
  );

  useUnsavedChanges(validation.dirty);
  const changeFirm = (firmId) => {
    setCanEditSharedProfile(false);
    validation.setFieldValue("firmId", firmId);
    validation.setFieldValue("branchId", "");
    if (!isEdit) { setLookup(null); setLookupError(""); }
    if (!firmId) return setLocations([]);
    showLoading(true);
    Promise.all([getCompanySpecificLocations(firmId), isEdit ? getData(`/User/userdetail/${userId}?firmId=${firmId}`) : Promise.resolve(null)])
      .then(([response, detail]) => {
        setLocations(response?.data || []);
        if (isEdit) {
          if (!detail?.status) throw new Error(detail?.message || "Membership not found in this firm.");
          setCanEditSharedProfile(detail.data.canEditSharedProfile === true);
          validation.setFieldValue("fullName", detail.data.fullName || "");
          validation.setFieldValue("mobileNo", detail.data.mobileNo || "");
          validation.setFieldValue("roleId", detail.data.roleId);
          validation.setFieldValue("branchId", detail.data.branchId);
          validation.setFieldValue("disableUser", detail.data.status !== 1);
        }
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify("Unable to load firm branches.");
      })
      .finally(() => showLoading(false));
  };

  return (
    <Form
      className="standard-drawer-form"
      onSubmit={(event) => {
        event.preventDefault();
        validation.handleSubmit();
      }}
    >
      <div className="standard-drawer-form__content">
        <p className="standard-drawer-form__intro">
          {isEdit
            ? canEditSharedProfile
              ? "You can update this user's name and contact number across all firms, and manage their access to this firm."
              : "Update this user's role, branch, and access to this firm. Their name and contact number are managed by the user or their mother firm's administrator."
            : "Invite a pre-authorized user and assign their firm, role, and branch access."}
        </p>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-user" />
              User Information
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          {currentFirmId === 1 && (
            <FormGroup>
              <Label htmlFor="user-firm">
                Firm <b>*</b>
              </Label>
              <Input
                id="user-firm"
                disabled={isEdit || lookupBusy}
                name="firmId"
                type="select"
                value={validation.values.firmId}
                onChange={(event) => changeFirm(event.target.value)}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.firmId && validation.errors.firmId
                )}
              >
                <option value="">Select firm</option>
                {firms.map((firm) => (
                  <option key={firm.id} value={firm.id}>
                    {firm.name}
                  </option>
                ))}
              </Input>
              {validation.touched.firmId && validation.errors.firmId && (
                <FormFeedback>{validation.errors.firmId}</FormFeedback>
              )}
            </FormGroup>
          )}
          <FormGroup>
            <Label htmlFor="user-email">
              Email Address (Login) <b>*</b>
            </Label>
            <Input
              id="user-email"
              disabled={isEdit || lookupBusy}
              name="emailId"
              type="email"
              placeholder="Enter email address"
              value={validation.values.emailId}
              onChange={(event) => {
                if (existingIdentity) {
                  validation.setFieldValue("fullName", "");
                  validation.setFieldValue("mobileNo", "");
                }
                setLookup(null); setLookupError(""); validation.handleChange(event);
              }}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.emailId && validation.errors.emailId
              )}
            />
            {validation.touched.emailId && validation.errors.emailId && (
              <FormFeedback>{validation.errors.emailId}</FormFeedback>
            )}
          </FormGroup>
          {!isEdit && <div className="mb-3">
            <Button type="button" disabled={lookupBusy || !validation.values.emailId || !(validation.values.firmId || currentFirmId)} onClick={() => checkIdentity()}>
              {lookupBusy ? "Checking�" : "Check user"}
            </Button>
            {lookupError && <p role="alert" className="text-danger mt-2">{lookupError}</p>}
            <div className="d-flex align-items-center gap-2 mt-2">
            {lookup && <p role="status" className="mb-0">{lookup.state === "new" ? "New PIERS user. Complete their profile and sign-in setup."
              : lookup.state === "existing" ? "Existing PIERS identity. Only a membership in this firm will be added."
              : lookup.state === "already_member" ? "This user already exists in the selected firm. Use Edit User to update their firm membership, or use the Edit option in the Users grid."
              : "Administrator reconciliation or explicit membership reactivation is required."}</p>}
            {lookup?.state === "already_member" && lookup.userId && onEditUser && (
              <Button
                type="button"
                color="primary"
                outline
                size="sm"
                className="flex-shrink-0"
                disabled={lookupBusy || Boolean(lookupError) || lookup.email !== validation.values.emailId.trim().toLowerCase() || lookup.firmId !== Number(validation.values.firmId || currentFirmId)}
                onClick={() => onEditUser({ id: lookup.userId, firmId: lookup.firmId })}
              >
                Edit User
              </Button>
            )}
            </div>
          </div>}
          <FormGroup>
            <Label htmlFor="user-name">
              Full Name <b>*</b>
            </Label>
            <Input
              id="user-name"
              disabled={detailsDisabled || (isEdit ? !canEditSharedProfile : Boolean(existingIdentity))}
              name="fullName"
              placeholder="Enter full name"
              value={validation.values.fullName}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.fullName && validation.errors.fullName
              )}
            />
            {validation.touched.fullName && validation.errors.fullName && (
              <FormFeedback>{validation.errors.fullName}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="user-designation">
              Designation <b>*</b>
            </Label>
            <Input
              id="user-designation" disabled={detailsDisabled}
              name="designation"
              placeholder="Enter designation"
              value={validation.values.designation}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.designation && validation.errors.designation
              )}
            />
            {validation.touched.designation &&
              validation.errors.designation && (
                <FormFeedback>{validation.errors.designation}</FormFeedback>
              )}
          </FormGroup>
        </section>
        <section className="standard-drawer-form__section">
          <div className="standard-drawer-form__section-title">
            <span>
              <i className="bx bx-shield-quarter" />
              Access and Contact
            </span>
            <i className="bx bx-chevron-down" />
          </div>
          <FormGroup>
            <Label htmlFor="user-mobile">
              Mobile / Contact Number <b>*</b>
            </Label>
            <Input
              id="user-mobile"
              disabled={detailsDisabled || (isEdit ? !canEditSharedProfile : Boolean(existingIdentity))}
              name="mobileNo"
              placeholder="Enter contact number"
              value={validation.values.mobileNo}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.mobileNo && validation.errors.mobileNo
              )}
            />
            {validation.touched.mobileNo && validation.errors.mobileNo && (
              <FormFeedback>{validation.errors.mobileNo}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="user-branch">
              Branch <b>*</b>
            </Label>
            <Input
              id="user-branch" disabled={detailsDisabled}
              name="branchId"
              type="select"
              value={validation.values.branchId}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.branchId && validation.errors.branchId
              )}
            >
              <option value="">Select branch</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </Input>
            {validation.touched.branchId && validation.errors.branchId && (
              <FormFeedback>{validation.errors.branchId}</FormFeedback>
            )}
          </FormGroup>
          <FormGroup>
            <Label htmlFor="user-role">
              Role in this firm <b>*</b>
            </Label>
            <Input
              id="user-role" disabled={detailsDisabled}
              name="roleId"
              type="select"
              value={validation.values.roleId}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              invalid={Boolean(
                validation.touched.roleId && validation.errors.roleId
              )}
            >
              <option value="">Select role</option>
              {roles.filter((role) => Number(validation.values.firmId || currentFirmId) === 1 || !/^(System|PIERS)/i.test(role.name)).map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </Input>
            {validation.touched.roleId && validation.errors.roleId && (
              <FormFeedback>{validation.errors.roleId}</FormFeedback>
            )}
          </FormGroup>
        </section>
        {needsPassword ? (
          <section className="standard-drawer-form__section">
            <div className="standard-drawer-form__section-title">
              <span>
                <i className="bx bx-lock-alt" />
                Sign-in Password
              </span>
              <i className="bx bx-chevron-down" />
            </div>
            <FormGroup>
              <Label htmlFor="user-password">
                Password <b>*</b>
              </Label>
              <Input
                id="user-password" disabled={detailsDisabled}
                name="newPassword"
                type="password"
                placeholder="Enter password"
                value={validation.values.newPassword}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.newPassword &&
                    validation.errors.newPassword
                )}
              />
              {validation.touched.newPassword &&
                validation.errors.newPassword && (
                  <FormFeedback>{validation.errors.newPassword}</FormFeedback>
                )}
            </FormGroup>
            <FormGroup>
              <Label htmlFor="user-confirm-password">
                Confirm Password <b>*</b>
              </Label>
              <Input
                id="user-confirm-password" disabled={detailsDisabled}
                name="confPassword"
                type="password"
                placeholder="Confirm password"
                value={validation.values.confPassword}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={Boolean(
                  validation.touched.confPassword &&
                    validation.errors.confPassword
                )}
              />
              {validation.touched.confPassword &&
                validation.errors.confPassword && (
                  <FormFeedback>{validation.errors.confPassword}</FormFeedback>
                )}
            </FormGroup>
          </section>
        ) : isEdit ? (
          <section className="standard-drawer-form__section">
            <div className="standard-drawer-form__section-title">
              <span>
                <i className="bx bx-cog" />
                Account Controls
              </span>
              <i className="bx bx-chevron-down" />
            </div>
            {[
              ["disableUser", "Remove access to this firm"],
            ].map(([field, label]) => (
              <div key={field} className="form-check form-switch mb-3">
                <Input
                  id={field}
                  name={field}
                  type="switch"
                  checked={Boolean(validation.values[field])}
                  onChange={validation.handleChange}
                />
                <Label className="form-check-label" htmlFor={field}>
                  {label}
                </Label>
              </div>
            ))}
          </section>
        ) : null}
        {!isEdit && <section className="standard-drawer-form__section" hidden>
          <p>For corporate SSO, enter the configured provider and source identity object ID. Missing identity details require administrator reconciliation.</p>
          <FormGroup><Label htmlFor="sso-provider">SSO provider ID</Label><Input id="sso-provider" disabled={detailsDisabled} name="sourceIdentityProvider" value={validation.values.sourceIdentityProvider || ""} onChange={validation.handleChange} /></FormGroup>
          <FormGroup><Label htmlFor="sso-object">Source identity object ID</Label><Input id="sso-object" disabled={detailsDisabled} name="sourceIdentityObjectId" value={validation.values.sourceIdentityObjectId || ""} onChange={validation.handleChange} /></FormGroup>
        </section>}
      </div>
      <footer className="standard-drawer-form__actions">
        <Button
          className="standard-drawer-form__cancel"
          type="button"
          onClick={closeAction}
        >
          Cancel
        </Button>
        <Button className="standard-drawer-form__submit" type="submit" disabled={detailsDisabled || lookupBusy}>
          {isEdit ? canEditSharedProfile ? "Save user details" : "Save firm access" : existingIdentity ? "Add to firm" : "Create and invite"}
        </Button>
      </footer>
    </Form>
  );
};

export default UserDetail;
